/**
 * TenantsTemplate - Pure UI component for Tenants page
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Layers } from 'lucide-react'
import { TenantTable } from '@/features/tenants/components/TenantTable'
import { CreateTenantDialog } from '@/features/tenants/components/CreateTenantDialog'
import { Button } from '@/components/ui/button'
import { Tenant } from '@/features/tenants/api'

export interface TenantsTemplateProps {
  tenants: Tenant[]
  total: number
  page: number
  limit: number
  totalPages: number
  isLoading: boolean
  error: Error | null
  createDialog: boolean
  onSetCreateDialog: (open: boolean) => void
  onPageChange: (page: number) => void
}

export function TenantsTemplate({
  tenants,
  total,
  page,
  limit,
  totalPages,
  isLoading,
  error,
  createDialog,
  onSetCreateDialog,
  onPageChange
}: TenantsTemplateProps) {
  return (
    <>
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground">
              {total === 1 ? 'tenant' : 'tenants'} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
            <Badge variant="default" className="h-4 w-4 rounded-full p-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tenants.filter(t => t.isActive).length}
            </div>
            <p className="text-xs text-muted-foreground">currently active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Page</CardTitle>
            <Badge variant="outline" className="h-4 w-4 rounded-full p-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{page}</div>
            <p className="text-xs text-muted-foreground">of {totalPages} pages</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Showing</CardTitle>
            <Badge variant="secondary" className="h-4 w-4 rounded-full p-0" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenants.length}</div>
            <p className="text-xs text-muted-foreground">tenants</p>
          </CardContent>
        </Card>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load tenants. Please try again.</AlertDescription>
        </Alert>
      )}

      {/* Tenants Table */}
      <TenantTable tenants={tenants} isLoading={isLoading} />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} tenants
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isLoading} onClick={() => onPageChange(page - 1)}>
              Previous
            </Button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    disabled={isLoading}
                    className="w-8 h-8 p-0"
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                )
              })}
            </div>
            <Button variant="outline" size="sm" disabled={page >= totalPages || isLoading} onClick={() => onPageChange(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <CreateTenantDialog open={createDialog} onOpenChange={onSetCreateDialog} />
    </>
  )
}
